import { Link } from "react-router-dom";
import { WalletShell, NAVY, NAVY_DARK, GOLD } from "@/components/wallet/WalletShell";
import { ArrowRight, TrendingUp, PiggyBank, Umbrella, Landmark, LineChart, ShieldCheck } from "lucide-react";

/**
 * WalletInvestmentsHub — mockup demonstrating Altara's role in the
 * investment-products journey. Static data by design. Illustrates
 * how Trust Requests, Trust Credentials and Outcome Governance
 * apply to Unit Trusts, TFSA, RA, and Living Annuities.
 */

const SCENARIOS = [
    {
        slug: "unit-trust",
        icon: LineChart,
        product: "Unit Trust",
        title: "Opening a discretionary Unit Trust",
        blurb: "New customer opens an equity Unit Trust with an asset manager. Altara orchestrates KYC, FAIS risk profiling, affordability and suitability — every step evidenced.",
        market: "R 3.5 trillion AUM (SA CIS industry, 2025)",
        tone: "sky",
    },
    {
        slug: "tfsa",
        icon: PiggyBank,
        product: "Tax-Free Savings Account",
        title: "TFSA onboarding + annual limit governance",
        blurb: "Consumer opens a TFSA and Altara issues a portable credential proving they haven't breached the R36k annual / R500k lifetime limit — reusable across providers.",
        market: "R 84 bn contributions 2024 across ± 3.4 m TFSA accounts",
        tone: "emerald",
    },
    {
        slug: "retirement-annuity",
        icon: Umbrella,
        product: "Retirement Annuity",
        title: "RA suitability + Regulation 28 monitoring",
        blurb: "Consumer opens an RA. Altara evidences FAIS advice, confirms Section 10C tax deduction limits and continuously monitors Regulation 28 compliance.",
        market: "R 1.1 trillion RA + preservation industry assets",
        tone: "amber",
    },
    {
        slug: "living-annuity",
        icon: Landmark,
        product: "Living Annuity",
        title: "Retirement drawdown — annual review outcome",
        blurb: "At-retirement conversion into a Living Annuity. Altara runs the annual drawdown-rate review and emits an Outcome Governance record confirming sustainability.",
        market: "Post-retirement — ± 250k active LA policies in SA",
        tone: "rose",
    },
];

const TONE = {
    sky:     { chip: "bg-sky-50 text-sky-700 border-sky-200",         accent: "#0284C7" },
    emerald: { chip: "bg-emerald-50 text-emerald-700 border-emerald-200", accent: "#059669" },
    amber:   { chip: "bg-amber-50 text-amber-800 border-amber-200",   accent: "#D97706" },
    rose:    { chip: "bg-rose-50 text-rose-700 border-rose-200",       accent: "#E11D48" },
};


export default function WalletInvestmentsHub() {
    return (
        <WalletShell activeTab="investments">
            <div className="max-w-5xl mx-auto px-4 lg:px-10 py-8 lg:py-10 space-y-8" data-testid="wallet-investments-hub">
                {/* Hero */}
                <section
                    className="rounded-3xl overflow-hidden shadow-xl relative"
                    style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_DARK} 65%, #020814 100%)` }}
                >
                    <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full" style={{ background: `${GOLD}18` }} />
                    <div className="relative px-6 lg:px-12 py-10 lg:py-12 text-white">
                        <div className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: GOLD }}>
                            Investment product scenarios
                        </div>
                        <h1 className="font-display text-3xl lg:text-5xl font-semibold mt-2 leading-tight">
                            Trust, from onboarding to<br />retirement drawdown.
                        </h1>
                        <p className="mt-4 text-white/70 max-w-2xl leading-relaxed">
                            Every investment-product journey is a series of trust obligations. Altara orchestrates them,
                            evidences them, and continuously monitors the outcomes — across every asset manager and platform.
                        </p>
                        <div className="mt-6 flex flex-wrap items-center gap-4 text-[11px] font-mono text-white/60">
                            <span className="uppercase tracking-widest">Unit Trusts</span>
                            <span>·</span>
                            <span className="uppercase tracking-widest">TFSA</span>
                            <span>·</span>
                            <span className="uppercase tracking-widest">Retirement Annuities</span>
                            <span>·</span>
                            <span className="uppercase tracking-widest">Living Annuities</span>
                        </div>
                    </div>
                </section>

                <section data-testid="investments-scenarios">
                    <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-slate-500 mb-4">
                        Choose a scenario
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        {SCENARIOS.map(s => (
                            <Link
                                key={s.slug}
                                to={`/wallet/investments/${s.slug}`}
                                data-testid={`investment-scenario-${s.slug}`}
                                className="group block border border-slate-200 rounded-2xl bg-white p-5 hover:border-slate-400 hover:shadow-sm transition-all"
                            >
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-10 h-10 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center">
                                        <s.icon className="w-4 h-4" style={{ color: TONE[s.tone].accent }} />
                                    </div>
                                    <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-1 rounded border ${TONE[s.tone].chip}`}>
                                        {s.product}
                                    </span>
                                </div>
                                <h3 className="font-display text-lg font-semibold text-slate-900 leading-snug">{s.title}</h3>
                                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.blurb}</p>
                                <div className="mt-4 flex items-center justify-between text-xs">
                                    <span className="text-slate-500 font-mono">{s.market}</span>
                                    <span className="inline-flex items-center gap-1 text-slate-700 group-hover:text-slate-900">
                                        Open <ArrowRight className="w-3.5 h-3.5" />
                                    </span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>

                {/* Value strip */}
                <section className="border border-slate-200 rounded-2xl bg-white p-6">
                    <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-slate-500">
                        Why this matters
                    </div>
                    <div className="mt-3 grid md:grid-cols-3 gap-6">
                        <ValueCell
                            icon={ShieldCheck}
                            title="FAIS-ready evidence"
                            body="Every advice interaction is captured as immutable evidence. Regulator-inspectable, consumer-portable."
                        />
                        <ValueCell
                            icon={TrendingUp}
                            title="Reuse across providers"
                            body="A KYC or FAIS Risk Profile issued once becomes a Trust Credential valid across every asset manager."
                        />
                        <ValueCell
                            icon={LineChart}
                            title="Continuous outcome monitoring"
                            body="Annual reviews (Reg 28, TFSA limits, LA drawdown) are Outcome Governance evaluations — not paperwork."
                        />
                    </div>
                </section>
            </div>
        </WalletShell>
    );
}


function ValueCell({ icon: Icon, title, body }) {
    return (
        <div>
            <div className="w-9 h-9 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center mb-3">
                <Icon className="w-4 h-4 text-slate-600" />
            </div>
            <div className="font-display text-sm font-semibold text-slate-900">{title}</div>
            <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{body}</p>
        </div>
    );
}
